<script context="module">
    import { apiMethods } from "../../../Services/index.svelte";
    import {
        defaultCalendarEventColor,
        DirectCalendarEventTypes,
        FallBackEncounterDurationMins,
    } from "../../../Utils/utilConstants.svelte";
    import { CalendarEventCategoryTypes } from "../../../Utils/utilEnums.svelte";
    import {
        getAccountCalendarEventType,
        getContrastBlackOrWhiteColor,
        getDateFromDateString,
        getDisplayPatientName,
        getDisplaySurgeonName,
        getDoctorName,
        getDoctorNameShortcut,
        getEventColor,
        getTitle,
    } from "../../../Utils/utilFunctions.svelte";
    import { DefaultColumnWidthPx } from "./CalendarConstants.svelte";

    export const handleNestedEvents = (events = [], encounters = []) => {
        let res = {
            events,
            restEncounters: encounters,
        };

        if (!events || !encounters) return res;

        if (events && events.length) {
            events.sort((a, b) => a.doctorId - b.doctorId);
        }

        const encounterMapDoctorId = new Map();

        for (const encounter of encounters) {
            const { doctorId } = encounter;

            if (!encounterMapDoctorId.has(doctorId))
                encounterMapDoctorId.set(doctorId, [encounter]);
            else encounterMapDoctorId.get(doctorId).push(encounter);
        }

        events.map((event) => {
            event.nestedEncounter = [];

            const addedEncounterIDs = [];

            const { eventType, doctorId, fromDate, toDate } = event;

            if (!eventType) return;

            const relatedEncounters = encounterMapDoctorId.get(doctorId);

            if (!relatedEncounters || !relatedEncounters.length) return;

            relatedEncounters.map((rEncounter) => {
                const { id, scheduledDate, dischargeDate, surgeonId, hospitalId } = rEncounter;
                //check if encounter overlaps with event and if surgeon or hospital matches (if applicable)
                if(event.surgeonId && event.surgeonId !== surgeonId) return;
                if(event.hospitalId && event.hospitalId !== hospitalId) return;

                if (
                    _checkEncounterOverlapEvents(
                        scheduledDate,
                        dischargeDate,
                        fromDate,
                        toDate,
                    )
                ) {
                    addedEncounterIDs.push(id);
                    event.nestedEncounter = event.nestedEncounter
                        ? [...event.nestedEncounter, rEncounter]
                        : [rEncounter];
                }
            });

            if (addedEncounterIDs.length)
                encounterMapDoctorId.set(
                    doctorId,
                    relatedEncounters.filter(
                        (encounter) =>
                            !addedEncounterIDs.includes(encounter.id),
                    ),
                );

            return event;
        });

        res = {
            events,
            restEncounters: Array.from(encounterMapDoctorId.values()).flatMap(
                (arr) => arr,
            ),
        };

        return res;
    };

    export const getPatientAge = (bd) => {
        const birthDate = new Date(bd);
        const currentDate = new Date();

        let age = currentDate.getFullYear() - birthDate.getFullYear();

        if (
            currentDate.getMonth() < birthDate.getMonth() ||
            (currentDate.getMonth() === birthDate.getMonth() &&
                currentDate.getDate() < birthDate.getDate())
        ) {
            age--;
        }

        return age;
    };

    export const handleSelectedTimeRangeDetails = (e) => {
        if (!e.hasOwnProperty("dateStr")) {
            const { allDay } = e;

            if (!allDay) return e;

            return {
                ...e,
                end: new Date(_getTimeWithPrevSec(e.endStr)),
                endStr: _getTimeWithPrevSec(e.endStr),
            };
        } else {
            let dateTime = new Date(e.dateStr);

            dateTime.setMinutes(dateTime.getMinutes() + 15);

            const year = dateTime.getFullYear();
            const month = ("0" + (dateTime.getMonth() + 1)).slice(-2);
            const day = ("0" + dateTime.getDate()).slice(-2);
            const hours = ("0" + dateTime.getHours()).slice(-2);
            const minutes = ("0" + dateTime.getMinutes()).slice(-2);

            return {
                ...e,
                startStr: e.dateStr,
                endStr: `${year}-${month}-${day}T${hours}:${minutes}:00`,
            };
        }
    };

    export const getTooltipContent = (
        item,
        startDate,
        endDate,
        showAdditionalInfo = true,
        isMonthEvents = false,
        tooltipPosition,
        isDoctorCalendar,
        viewType,
    ) => {
        const showCollapsedTooltip =
            isMonthEvents &&
            item.hasOwnProperty("timeArr") &&
            item.timeArr.length > 1;

        const shortTime = new Intl.DateTimeFormat("en-AU", {
            hour: "numeric",
            minute: "2-digit",
            hourCycle: "h24",
        });
        const fromTime = shortTime.format(startDate).replace(/ .*$/, "");
        const endTime = shortTime.format(endDate);

        const doctor = `${
            item.doctor?.title === 20 ? "" : item.doctor?.titleName
        } ${item.doctor?.firstName} ${item.doctor?.lastName}`;
        const patient = `${
            item.patient?.title === 20 ? "" : item.patient?.titleName
        } ${item.patient?.firstName} ${item.patient?.lastName}`;
        const desc = item.procedureName ? `, ${item.procedureName}` : "";

        const fullTitle = `${fromTime} - ${endTime} ${doctor}: ${patient}${desc}`;

        const hospital = item.hospital ? item.hospital.name : "";

        const surgeon = item.surgeon
            ? `${getTitle(item.surgeon.title)} ${item.surgeon.firstName} ${
                  item.surgeon.lastName
              }`
            : "";

        const mergedEncounterText = item.isMergedEncounter
            ? (item.mergedEncounters?.length ?? 0)
            : "";

        const content = showCollapsedTooltip
            ? _getTooltipContent(item, isDoctorCalendar)
            : "";

        const nodeColor =
            getEventColor(CalendarEventCategoryTypes.PatientEncounter) ??
            defaultCalendarEventColor;

        return {
            html: showCollapsedTooltip
                ? `<div class="relative h-full tooltip-trigger">
                        <div class="tooltip-trigger overflow-hidden flex flex-col">
                            ${
                                isDoctorCalendar
                                    ? `
                                        <p>${
                                            hospital ?? "Missing hospital name"
                                        }</p>
                                        <p><strong>Time:</strong> ${fromTime} - ${endTime}</p>
                                        <p><strong>Surgeon:</strong> ${surgeon}</p>
                                    `
                                    : `<p>${fullTitle}</p>`
                            }
                        </div>
                    </div>`
                : `<div class="relative h-full tooltip-trigger">
                        <div class="tooltip-trigger overflow-hidden flex flex-col h-full">
                            ${
                                isDoctorCalendar
                                    ? `
                                        <p>${
                                            hospital ?? "Missing hospital name"
                                        }</p>
                                        <p><strong>Time:</strong> ${fromTime} - ${endTime}</p>
                                        <p><strong>Surgeon:</strong> ${surgeon}</p>
                                    `
                                    : `
                                        ${
                                            viewType == "dayGridMonth"
                                                ? fullTitle
                                                : `
                                            <div class="flex gap-x-2">
                                                <i class="fa fa-hand-holding-medical"></i>
                                                <div class="flex flex-col">
                                                    <p><strong>${fromTime} - ${endTime}</strong></p>
                                                    <p style="text-wrap: wrap;"><strong>Doctor:</strong> ${doctor}</p>
                                                    <p style="text-wrap: wrap;"><strong>Surgeon:</strong> ${surgeon}</p>
                                                    <p class="${mergedEncounterText ? "" : "hidden"}">Encounters: ${mergedEncounterText ? mergedEncounterText : "-"}</p>
                                                </div>
                                            </div>
                                        `
                                        }
                                    `
                            }
                        </div>
                    </div>`,
        };
    };

    export const getTooltipPosition = (start, end, viewType, view, index) => {
        let position = [];

        const dayOfWeek = start.getDay();
        const year = start.getFullYear();
        const month = start.getMonth();
        const date = start.getDate();
        const hour = end.getHours();

        switch (viewType) {
            case "dayGridMonth":
                if (dayOfWeek < 3) position.push("custom-tooltipRight");
                else if (dayOfWeek > 3) position.push("custom-tooltipLeft");

                const firstDayOfMonth = new Date(year, month, 1);
                const firstDayOfWeek = firstDayOfMonth.getDay();
                const row = Math.ceil((date + firstDayOfWeek) / 7);
                const isEventCurrentMonth =
                    view.currentStart.getMonth() == month;
                const isEventNextMonth = view.currentStart.getMonth() < month;

                position.push(
                    isEventCurrentMonth
                        ? row <= 3
                            ? "custom-tooltipBottom"
                            : "custom-tooltipTop"
                        : isEventNextMonth
                          ? "custom-tooltipTop"
                          : "custom-tooltipBottom",
                );
                return position.join(" ");
            case "timeGridWeek":
                if (dayOfWeek < 3) position.push("custom-tooltipRight");
                else if (dayOfWeek > 3) position.push("custom-tooltipLeft");

                if (hour < 5) position.push("custom-tooltipBottom");
                else position.push("custom-tooltipTop");
                return position.join(" ");
            case "timeGridDay":
                if (hour < 5) position.push("custom-tooltipBottom");
                else position.push("custom-tooltipTop");
                return position.join(" ");
            case "listWeek":
                if (!index) position.push("custom-tooltipBottom");
                else position.push("custom-tooltipTop");
                return position.join(" ");
            default:
                return;
        }
    };

    export const createDoctorEvent = (event, viewType, isDoctorCalendar) => {
        const {
            fromDate,
            toDate,
            doctorId,
            doctorName,
            eventType,
            id,
            description,
            nestedEncounter,
            hospitalId,
            hospitalState,
            surgeonId,
        } = event;

        let eventCategory = {};

        if (
            event.eventCategory &&
            event.eventCategoryColour &&
            event.eventCategoryName
        ) {
            eventCategory.color = event.eventCategoryColour;
            eventCategory.name = event.eventCategoryName;
        }

        const fDate = new Date(fromDate);
        const tDate = new Date(toDate);
        const fromTime = fromDate.split("T")[1].slice(0, 5);
        const toTime = toDate.split("T")[1].slice(0, 5);

        const startDateArr = getDateFromDateString(fDate).split(", ");
        const startDate =
            startDateArr[0].split("/").reverse().join("-") +
            ", " +
            startDateArr[1];
        const endDateArr = getDateFromDateString(tDate).split(", ");
        const endDate =
            endDateArr[0].split("/").reverse().join("-") + ", " + endDateArr[1];

        const eventColor = getEventColor(event);

        const titleSurgeonContent = event.surgeonName
            ? `${event.surgeonName} `
            : "";

        const titleHospitalContent = event.hospitalName
            ? `${event.hospitalName} `
            : "";

        const getMonthViewTitle = () => {
            switch (eventType) {
                // show Surgeon & hospital for month view list items (if applicable)
                case DirectCalendarEventTypes.List:
                    return `${titleSurgeonContent}${titleHospitalContent}${getAccountCalendarEventType(
                        eventType,
                    )}: ${description} ${
                        eventType
                            ? `(encounters: ${nestedEncounter?.length ?? 0})`
                            : ""
                    }`;
                default:
                    return `${doctorName} ${getAccountCalendarEventType(
                        eventType,
                    )}: ${description} ${
                        eventType
                            ? `(encounters: ${nestedEncounter?.length ?? 0})`
                            : ""
                    }`;
            }
        };

        const singleLineContent = getMonthViewTitle();

        const multiLineContent = _getEventContent(
            doctorName,
            eventType,
            nestedEncounter,
            description,
            fromTime,
            toTime,
            eventCategory,
            event,
            isDoctorCalendar,
        );

        const displayContent = viewType
            ? viewType == "dayGridMonth"
                ? singleLineContent
                : multiLineContent
            : singleLineContent;

        // const hospitalName =
        //     (event.hospitalName ?? nestedEncounter.length)
        //         ? (nestedEncounter[0]?.hospital?.name ?? "")
        //         : "";
        const hospitalName = event.hospitalName ?? "";

        // const surgeonName =
        //     (event.surgeonName ?? nestedEncounter.length)
        //         ? getDisplaySurgeonName(nestedEncounter[0]?.surgeon)
        //         : "";
        const surgeonName = event.surgeonName ?? "";

        return {
            id: doctorId,
            start: startDate,
            end: endDate,
            title: displayContent,
            resourceId: doctorId,
            color: eventColor,
            editable: true,
            allDay: _isAllDayEvent(startDate, endDate),
            extendedProps: {
                ...event,
                eventType,
                eventId: id,
                doctorId,
                hospitalId,
                surgeonId,
                nestedEncounter: nestedEncounter ?? [],
                doctorName,
                doctorNameSc: getDoctorNameShortcut(doctorName),
                startDate: startDateArr,
                endDate: endDateArr,
                eventColor,
                fromDate,
                toDate,
                hospitalName,
                hospitalState,
                surgeonName,
                description,
            },
        };
    };

    export const resetCalendarRecurrenceEvent = (event) => ({
        ...event,
        repeatDaysOfWeek: [],
        repeatDaysOfMonth: [],
        repeatDaysOfYear: [],
        repeatInterval: 0,
        repeatIntervalType: 0,
        repeatEnd: null,
        parentId: 0,
    });

    export const syncMapItem = async (
        item,
        index,
        isMonthEvents,
        getTooltipContent,
        isDoctorView,
        doctorsColors,
        eventColour,
    ) => {
        const __min_event_time__ = FallBackEncounterDurationMins;

        let startDate, endDate;
        let scheduledDate, dischargeDate;

        let lastStartTime = 0;

        if (!item.scheduledDate && !item.dischargeDate) {
            scheduledDate = new Date();

            dischargeDate = new Date(
                scheduledDate.getTime() + __min_event_time__ * 60 * 1000,
            );
        } else if (!item.scheduledDate) {
            dischargeDate = new Date(item.dischargeDate);

            scheduledDate = new Date(
                dischargeDate.getTime() - __min_event_time__ * 60 * 1000,
            );
        } else if (!item.dischargeDate) {
            scheduledDate = new Date(item.scheduledDate);

            dischargeDate = new Date(
                scheduledDate.getTime() + __min_event_time__ * 60 * 1000,
            );
        } else {
            scheduledDate = new Date(item.scheduledDate);

            if (lastStartTime === scheduledDate) {
                scheduledDate = new Date(scheduledDate.getTime() + 2000);
            }
            lastStartTime = scheduledDate.getTime();

            dischargeDate =
                item.dischargeDate && item.scheduledDate < item.dischargeDate
                    ? new Date(item.dischargeDate)
                    : new Date(item.scheduledDate);

            if (
                (dischargeDate - scheduledDate) / (1000 * 60) <
                __min_event_time__
            )
                dischargeDate = new Date(
                    scheduledDate.getTime() + __min_event_time__ * 60 * 1000,
                );
        }

        const startDateArr = getDateFromDateString(scheduledDate).split(", ");

        const endDateArr = getDateFromDateString(dischargeDate).split(", ");

        startDate =
            startDateArr[0].split("/").reverse().join("-") +
            ", " +
            startDateArr[1];

        endDate =
            endDateArr[0].split("/").reverse().join("-") + ", " + endDateArr[1];

        let tooltipContent = await getTooltipContent(
            item,
            scheduledDate,
            dischargeDate,
            item.editable,
            isMonthEvents,
            index,
        );

        return {
            id: item.id,
            resourceId: item.doctorId,
            start: startDate,
            end: endDate,
            title: tooltipContent,
            color: getEventColor(CalendarEventCategoryTypes.PatientEncounter),
            doctorId: item.doctorId,
            hospitalId: item.hospitalId ?? 0,
            hospitalState: item.hospital?.state ?? "",
            editable: item.editable ?? true,
            allDay: _isAllDayEvent(startDate, endDate),
            extendedProps: {
                eventColor: getEventColor(
                    CalendarEventCategoryTypes.PatientEncounter,
                ),
                fromDate: scheduledDate,
                toDate: dischargeDate,
                eventType: "encounter",
                doctorId: item.doctorId,
                doctorName: item.doctorName ?? getDoctorName(item.doctor),
                doctorNameSc: getDoctorNameShortcut(
                    item.doctor ? item.doctor : (item.doctorName ?? ""),
                ),
                date: new Date(item.dischargeDate),
                encounterItem: item.scheduledDate ? item : undefined,
                mergedEncounters: item.isMergedEncounter
                    ? item.mergedEncounters
                    : [],
            },
        };
    };

    export const checkRecurrenceValidation = (eventOBj) => {
        const {
            repeatInterval,
            repeatIntervalType,
            repeatDaysOfWeek,
            repeatDaysOfMonth,
            selectedRepeatYearDay,
        } = eventOBj;

        const isMissingRepeatInterval = !repeatInterval || repeatInterval <= 0;

        if (isMissingRepeatInterval)
            return {
                isDisabled: true,
                text: "Missing Repeat Interval",
            };

        const res = {
            isDisabled: false,
            text: "",
        };

        switch (repeatIntervalType) {
            case 2:
                res.isDisabled = !repeatDaysOfWeek?.length;
                res.text = repeatDaysOfWeek?.length
                    ? ""
                    : "Missing Repeat Days of Week";
                break;
            case 3:
                res.isDisabled = !repeatDaysOfMonth?.length;
                res.text = repeatDaysOfMonth?.length
                    ? ""
                    : "Missing Repeat Days of Month";
                break;
            case 4:
                res.isDisabled = !selectedRepeatYearDay;
                res.text = selectedRepeatYearDay
                    ? ""
                    : "Missing Repeat Day of Year";
                break;
            default:
                return res;
        }

        return res;
    };

    export const syncCalendarEventDate = (data) => {
        const { fromDate, fromTime, toDate, toTime } = data;

        data.fromDate = fromDate.split("T")[0];
        data.toDate = toDate.split("T")[0];

        data.fromTime = fromTime
            ? fromTime
            : fromDate.split("T")[1].slice(0, 5);
        data.toTime = toTime ? toTime : toDate.split("T")[1].slice(0, 5);

        if (data.fromTime == "00:00" && data.toTime == "23:59")
            data.isFullDay = true;

        return data;
    };

    export const customizeEventStyle = () => {
        const allEventNodes = document.querySelectorAll(".ec-event");
        const allEventTitleNodes = document.querySelectorAll(".ec-event-title");
        if (allEventNodes.length) {
            allEventNodes.forEach((node) => {
                const nodeColor = node.style.backgroundColor
                    ? node.style.backgroundColor
                    : defaultCalendarEventColor;

                node.style.backgroundColor = "#fff";
                node.style.color = "#000";
                node.style.background = `linear-gradient(to right, ${nodeColor} 10px, #ffffff 10px)`;
                node.classList.add("border", "border-2");
                node.classList.remove("invisible");
            });
        }
        if (allEventTitleNodes.length) {
            allEventTitleNodes.forEach((node) => {
                node.style.paddingLeft = "15px";
                node.style.fontSize = "13px";
            });
        }
    };

    export const customizeWeekEventColWidth = () => {
        const allCols = document.querySelectorAll(".ec-body .ec-events");

        if (allCols.length) {
            const colClasses = [
                ".ec-sun",
                ".ec-mon",
                ".ec-tue",
                ".ec-wed",
                ".ec-thu",
                ".ec-fri",
                ".ec-sat",
            ];

            let defaultColWidth = 185;

            const container = document.getElementById("calendarContainer");
            const sidebars = document.getElementsByClassName("ec-sidebar");

            if (container && sidebars.length) {
                const calculatedWidth = Math.floor(
                    (container.clientWidth - sidebars[0].clientWidth) / 7,
                );
                defaultColWidth = Math.max(defaultColWidth, calculatedWidth);
            }

            allCols.forEach((col, i) => {
                const articles = col.querySelectorAll(".ec-event");
                const maxColWidth = 200;

                if (articles.length) {
                    let minColWidth = 100;

                    articles.forEach((art) => {
                        const width = parseFloat(
                            String(art.style.width).replace("%", ""),
                        );

                        if (width) minColWidth = Math.min(minColWidth, width);

                        art.style.maxWidth = `${maxColWidth}px`;
                    });

                    const targetCols = document.querySelectorAll(colClasses[i]);

                    if (minColWidth < 100) {
                        const totalCols = Math.floor(100 / minColWidth);

                        if (targetCols.length) {
                            targetCols.forEach((targetCol) => {
                                targetCol.style.width = `${totalCols * maxColWidth}px`;
                                targetCol.style.flex = "0 1 auto";
                            });
                        }
                    } else if (targetCols.length) {
                        targetCols.forEach((targetCol) => {
                            targetCol.style.width = `${defaultColWidth}px`;
                            targetCol.style.flex = "0 1 auto";
                        });
                    }
                } else {
                    const targetCols = document.querySelectorAll(colClasses[i]);
                    if (targetCols.length)
                        targetCols.forEach((targetCol) => {
                            targetCol.style.width = `${defaultColWidth}px`;
                            targetCol.style.flex = "0 1 auto";
                        });
                }
            });
        }

        setTimeout(() => {
            customizeWeekAllDayEventColWidth();
        }, 100);
    };

    export const customizeWeekAllDayEventColWidth = () => {
        const allCols = document.querySelectorAll(".ec-all-day  .ec-events");

        if (allCols.length) {
            allCols.forEach((col, i) => {
                const articles = col.querySelectorAll(".ec-event");

                if (articles.length) {
                    articles.forEach((art) => {
                        let days = art.style?.width?.substr(5, 1);
                        let calculatedWidthPx = 0;
                        try {
                            days = Number(days);
                        } catch (error) {
                            return;
                        }

                        // default row gap from calendar
                        calculatedWidthPx += days * 7;

                        while (days > 0) {
                            const containerWidth =
                                allCols[i + days - 1]?.clientWidth;
                            calculatedWidthPx += containerWidth;
                            days -= 1;
                        }

                        art.style.width = `${calculatedWidthPx}px`;
                    });
                }
            });
        }
    };

    export const loadEventMetadata = async (event) => {
        const eventMetadata = {};

        const searchObj = {};
        searchObj["filter[controller]"] = "AccountCalendarEvent";
        searchObj["filter[entityId]"] = event.id;
        
        var logsResponse = await apiMethods.getMethod
            .getActivityLogs(0, 999999, searchObj);
        if(logsResponse && logsResponse.results) {
            //find the created log
            var created = logsResponse.results.find((l) => l.actionTypeName == "POST");
            if(created){
                eventMetadata.createdOn = created.createdDate;
                eventMetadata.createdBy = created.userName;
            }

            //find the latest updated log
            const lastUpdate = logsResponse.results.filter((l) => l.actionTypeName == "PUT").reduce((latest, current) =>
                !latest || current.createdDate > latest.createdDate ? current : latest
            , null);
            if(lastUpdate){
                eventMetadata.updatedOn = lastUpdate.createdDate;
                eventMetadata.updatedBy = lastUpdate.userName;
            }
        }

        return eventMetadata;
    }

    export const customizeVerticalViewColWidthAndColor = (doctors) => {
        const allCols = document.querySelectorAll(".ec-body .ec-events");
        
        const resourceWrappers = document.querySelectorAll(".ec-resource");

        if (allCols.length) {
            let defaultColWidth = DefaultColumnWidthPx;

            const container = document.getElementById("calendarContainer");
            const sidebars = document.getElementsByClassName("ec-sidebar");

            if (container && sidebars.length) {
                const calculatedWidth = Math.floor(
                    (container.clientWidth - sidebars[0].clientWidth) /
                        allCols.length,
                );
                defaultColWidth = Math.max(defaultColWidth, calculatedWidth);
            }

            allCols.forEach((col, i) => {
                const articles = col.querySelectorAll(".ec-event");
                const maxColWidth = 200;
                const targetCols = [
                    resourceWrappers[i],
                    resourceWrappers[i + allCols.length],
                    resourceWrappers[i + allCols.length * 2],
                ];

                // for doctor name area color
                if (doctors[i]?.calendarColour) {
                    const textColor = getContrastBlackOrWhiteColor(
                        doctors[i]?.calendarColour,
                        undefined,
                        "#6F726C",
                    );
                    resourceWrappers[i].style.backgroundColor =
                        doctors[i]?.calendarColour;
                    resourceWrappers[i].style.color = textColor;
                } else {
                    resourceWrappers[i]?.style.removeProperty(
                        "background-color",
                    );
                    resourceWrappers[i]?.style.removeProperty("color");
                }
                // color change end

                if (articles.length) {
                    let minColWidth = 100;

                    articles.forEach((art) => {
                        const width = parseFloat(
                            String(art.style.width).replace("%", ""),
                        );

                        if (width) minColWidth = Math.min(minColWidth, width);

                        art.style.maxWidth = `${maxColWidth}px`;
                    });

                    if (minColWidth < 100) {
                        const totalCols = Math.floor(100 / minColWidth);

                        if (targetCols.length) {
                            targetCols.forEach((targetCol) => {
                                targetCol.style.width = `${totalCols * maxColWidth}px`;
                                targetCol.style.flex = "0 1 auto";
                            });
                        }
                    } else if (targetCols.length) {
                        targetCols.forEach((targetCol) => {
                            targetCol.style.width = `${defaultColWidth}px`;
                            targetCol.style.flex = "0 1 auto";
                        });
                    }
                } else {
                    if (targetCols.length)
                        targetCols.forEach((targetCol) => {
                            targetCol.style.width = `${defaultColWidth}px`;
                            targetCol.style.flex = "0 1 auto";
                        });
                }
            });
        }
    };

    export const customizeVerticalViewDoctorColor = () => {
        const allCols = document.querySelectorAll(".ec-body .ec-events");
        const resourceWrappers = document.querySelectorAll(".ec-resource");

        if (allCols.length) {
            let defaultColWidth = DefaultColumnWidthPx;

            const container = document.getElementById("calendarContainer");
            const sidebars = document.getElementsByClassName("ec-sidebar");

            if (container && sidebars.length) {
                const calculatedWidth = Math.floor(
                    (container.clientWidth - sidebars[0].clientWidth) /
                        allCols.length,
                );
                defaultColWidth = Math.max(defaultColWidth, calculatedWidth);
            }

            allCols.forEach((col, i) => {
                const articles = col.querySelectorAll(".ec-event");
                const maxColWidth = 200;
                const targetCols = [
                    resourceWrappers[i],
                    resourceWrappers[i + allCols.length],
                ];

                if (articles.length) {
                    let minColWidth = 100;

                    articles.forEach((art) => {
                        const width = parseFloat(
                            String(art.style.width).replace("%", ""),
                        );

                        if (width) minColWidth = Math.min(minColWidth, width);

                        art.style.maxWidth = `${maxColWidth}px`;
                    });

                    if (minColWidth < 100) {
                        const totalCols = Math.floor(100 / minColWidth);

                        if (targetCols.length) {
                            targetCols.forEach((targetCol) => {
                                targetCol.style.width = `${totalCols * maxColWidth}px`;
                                targetCol.style.flex = "0 1 auto";
                            });
                        }
                    } else if (targetCols.length) {
                        targetCols.forEach((targetCol) => {
                            targetCol.style.width = `${defaultColWidth}px`;
                            targetCol.style.flex = "0 1 auto";
                        });
                    }
                } else {
                    if (targetCols.length)
                        targetCols.forEach((targetCol) => {
                            targetCol.style.width = `${defaultColWidth}px`;
                            targetCol.style.flex = "0 1 auto";
                        });
                }
            });
        }
    };

    export const resetWeekEventColWidth = () => {
        const colClasses = [
            ".ec-sun",
            ".ec-mon",
            ".ec-tue",
            ".ec-wed",
            ".ec-thu",
            ".ec-fri",
            ".ec-sat",
        ];

        colClasses.map((colClass) => {
            const targetCols = document.querySelectorAll(colClass);

            targetCols.forEach((targetCol) => {
                targetCol.style.width = "100%";
                targetCol.style.flex = "1 1 auto";
            });
        });
    };

    export const setCalendarOverflow = () => {
        const calendarContainer = document.getElementById("calendarContainer");

        const timeGrid = calendarContainer.querySelector(".ec-time-grid");

        if (timeGrid.querySelector(".toolbar-wrapper")) return;

        const initialWidth = calendarContainer.offsetWidth - 24;

        if (!timeGrid.querySelector(".toolbarWrapper")) {
            const toolbarWrapper = document.createElement("div");
            toolbarWrapper.classList.add(
                "toolbarWrapper",
                "sticky",
                "top-0",
                "left-4",
                "bg-white",
            );

            const toolbar = timeGrid.querySelector(".ec-toolbar");
            toolbarWrapper.appendChild(toolbar);

            toolbarWrapper.style.width = initialWidth + "px";

            timeGrid.insertBefore(toolbarWrapper, timeGrid.firstChild);
        } else {
            const toolbarWrapper = timeGrid.querySelector(".toolbarWrapper");

            const toolbar = timeGrid.querySelector(".ec-toolbar");
            toolbarWrapper.appendChild(toolbar);

            toolbarWrapper.style.width = initialWidth + "px";

            timeGrid.insertBefore(toolbarWrapper, timeGrid.firstChild);
        }

        if (!timeGrid.querySelector(".scrollableWrapper")) {
            const scrollableWrapper = document.createElement("div");
            scrollableWrapper.classList.add(
                "scrollableWrapper",
                "overflow-y-auto",
                "overflow-x-hidden",
            );

            const header = timeGrid.querySelector(".ec-header");
            const allDay = timeGrid.querySelector(".ec-all-day");
            const body = timeGrid.querySelector(".ec-body");

            scrollableWrapper.appendChild(header);
            scrollableWrapper.appendChild(allDay);
            scrollableWrapper.appendChild(body);

            timeGrid.appendChild(scrollableWrapper);
        } else {
            const scrollableWrapper =
                timeGrid.querySelector(".scrollableWrapper");

            const header = timeGrid.querySelector(".ec-header");
            const allDay = timeGrid.querySelector(".ec-all-day");
            const body = timeGrid.querySelector(".ec-body");

            scrollableWrapper.appendChild(header);
            scrollableWrapper.appendChild(allDay);
            scrollableWrapper.appendChild(body);

            timeGrid.appendChild(scrollableWrapper);
        }
    };

    export const customizeEventTextTruncate = (isTruncate = false) => {
        const allEventBodies = document.querySelectorAll(".ec-event-body");

        if (allEventBodies)
            allEventBodies.forEach((node) => {
                if (isTruncate) node.classList.add("truncate");
                else node.classList.remove("truncate");
            });
    };

    export const getTimeAfterChanges = (d, milSec = 0) => {
        let res = new Date(d);

        res.setTime(res.getTime() + milSec);

        let year = res.getFullYear();
        let month = String(res.getMonth() + 1).padStart(2, "0");
        let day = String(res.getDate()).padStart(2, "0");
        let hours = String(res.getHours()).padStart(2, "0");
        let minutes = String(res.getMinutes()).padStart(2, "0");
        let seconds = String(res.getSeconds()).padStart(2, "0");

        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
    };

    // HELPER FUNCTIONS
    export const _checkEncounterOverlapEvents = (
        encounterStart,
        encounterEnd,
        listStart,
        listEnd,
    ) => {
        encounterStart = new Date(encounterStart);
        listStart = new Date(listStart);
        // encounterEnd = new Date(encounterEnd);
        listEnd = new Date(listEnd);
        // if (encounterStart > encounterEnd)
        //     encounterEnd = new Date(
        //         encounterStart.getTime() +
        //             FallBackEncounterDurationMins * 60 * 1000,
        //     );

        return encounterStart >= listStart && encounterStart <= listEnd;
    };

    const _getTooltipContent = ({ timeArr }, isDoctorCalendar) => {
        const arr = [];

        const __getTime = (time) => {
            const date = new Date(time);
            const hours = date.getHours().toString().padStart(2, "0");
            const minutes = date.getMinutes().toString().padStart(2, "0");

            return `${hours}:${minutes}`;
        };

        timeArr.map((encounter) => {
            if (!isDoctorCalendar)
                arr.push(
                    `<strong>Time:</strong> ${__getTime(
                        encounter.start,
                    )} - ${__getTime(encounter.end)}<br />
                    <strong>Surgeon:</strong> ${encounter.surgeon}<br />
                    <strong>Hospital:</strong> ${encounter.hospital}`,
                );
            else
                arr.push(
                    `
                    <strong>Patient:</strong> ${encounter.patientName}, ${
                        encounter.patientAge
                    }, ${
                        encounter.patientGender == 0
                            ? "Male"
                            : encounter.patientGender == 1
                              ? "Female"
                              : "Unknown"
                    }<br />
                    <strong>Procedure: ${encounter.patientProcedure}</strong>
                    `,
                );
        });

        return arr.join("<hr/>");
    };

    export const _isAllDayEvent = (time1, time2) => {
        const date1 = new Date(time1);
        const date2 = new Date(time2);

        const timeDiff = Math.abs(date2 - date1) / (1000 * 60 * 60);

        return timeDiff >= 23.9;
    };

    const _getTimeWithPrevSec = (d) => {
        const date = new Date(d);

        date.setSeconds(date.getSeconds() - 1);

        const year = date.getFullYear();
        const month = ("0" + (date.getMonth() + 1)).slice(-2);
        const day = ("0" + date.getDate()).slice(-2);
        const hours = ("0" + date.getHours()).slice(-2);
        const minutes = ("0" + date.getMinutes()).slice(-2);

        return `${year}-${month}-${day}T${hours}:${minutes}:00`;
    };

    const getEventIcon = (eventType) => {
        switch (eventType) {
            case DirectCalendarEventTypes.Leave:
                return "fa-solid fa-person-running";
            case DirectCalendarEventTypes.List:
                return "fa-solid fa-list";
            case DirectCalendarEventTypes.OnCall:
                return "fa-solid fa-bell";
            case DirectCalendarEventTypes.Global:
                return "fa-solid fa-globe";
            case DirectCalendarEventTypes.PublicHoliday:
                return "fas fa-mug-hot";
            default:
                return "";
        }
    };

    const _getEventContent = (
        doctorName,
        eventType,
        nestedEncounter,
        description,
        fromTime,
        toTime,
        eventCategory,
        event,
        isDoctorCalendar,
    ) => {
        return {
            html: ` <div class="h-full overflow-hidden">
                        <div class="flex gap-x-2">
                            <i class="${getEventIcon(eventType)} mt-[3px]"></i>
                            <div class="flex flex-col">
                                <p>${eventCategory.name ? "<span style='color: " + eventCategory.color + ";'>" + eventCategory.name + ": </span>" : ""}<strong>${getAccountCalendarEventType(eventType)} ${fromTime} - ${toTime}</strong></p>
                                ${isDoctorCalendar ? "" : "<p><strong>Doctor:</strong>" + doctorName + "</p>"}
                                ${renderListHospitalAndSurgeon(event)}
                                <p class=${nestedEncounter?.length ? "" : "hidden"}><strong>Encounters:</strong> ${nestedEncounter ? nestedEncounter.filter((e) => e.status != 9).length : 0}</p>
                                <p class=${nestedEncounter?.length ? "hidden" : ""}>Description: ${description ? description : "-"}</p>
                                ${renderNestedEncounters(nestedEncounter)}
                            </div>
                        </div>
                    </div>
                `,
        };
    };

    const renderNestedEncounters = (encounters) => {
        let renderedHtml = "";
        if (encounters && encounters.length) {
            encounters.filter((e) => e.status != 9).forEach((encounter) => {
                const fromTime = encounter.scheduledDate
                    ?.split("T")[1]
                    .slice(0, 5);
                const toTime = encounter.dischargeDate
                    ?.split("T")[1]
                    .slice(0, 5);
                const patientName = getDisplayPatientName(encounter.patient);
                const html = `
                    <div class="flex gap-x-2 mt-[6px]">
                        <i class="fa fa-hand-holding-medical mt-[3px]"></i>
                        <div class="flex flex-col">
                            <p><strong>${fromTime} - ${toTime}</strong></p>
                            <p class="one-line-text">${patientName}</p>
                            ${encounter.procedureName ? '<p class="one-line-text">' + encounter.procedureName + "</p>" : ""}
                        </div>
                    </div>
                    `;
                renderedHtml += html;
            });
        }
        return renderedHtml;
    };

    const renderListHospitalAndSurgeon = (event) => {
        let html = "";
        if (event && event.eventType === 1) {
            if (event.hospitalName)
                html += `<p><strong>Hospital:</strong> ${event.hospitalName}</p>`;
            if (event.surgeonName)
                html += `<p><strong>Surgeon:</strong> ${event.surgeonName}</p>`;
        }
        return html;
    };

    export const renderGlobalOrPublicHolidayTitle = (
        event,
        eventType,
        canUserEditGlobalEvents,
    ) => {
        switch (eventType) {
            case DirectCalendarEventTypes.Global:
                return {
                    html: ` <div class="h-full overflow-hidden ${canUserEditGlobalEvents ? "" : "cursorDefaultI"}">
                                <div class="flex gap-x-2">
                                    <i class="${getEventIcon(eventType)} mt-[3px]"></i>
                                    <p>${event.description}</p>
                                </div>
                            </div>
                        `,
                };
            case DirectCalendarEventTypes.PublicHoliday:
                return {
                    html: ` <div class="h-full overflow-hidden cursorDefaultI">
                                <div class="flex gap-x-2">
                                    <i class="${getEventIcon(eventType)} mt-[3px]"></i>
                                    <strong>${event.title}</strong>
                                </div>
                            </div>
                        `,
                };
            default:
                return "";
        }
    };
</script>

<style>
    :global(.one-line-text) {
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-line-clamp: 1; /* number of lines to show */
        line-clamp: 1;
        -webkit-box-orient: vertical;
    }
</style>
